import { createContext, useContext, useState, useEffect } from "react";
import React from "react";
import { safeStorage } from "../utils/storage";
import { formatMessage } from "../utils/formatMessage";

const LanguageContext = createContext();

const SUPPORTED_LANGUAGES = ["en", "es", "de", "fr", "it", "el", "pl", "pt", "tr"];

const getBrowserLanguage = () => {
  const browserLang = navigator.language.split("-")[0];
  return SUPPORTED_LANGUAGES.includes(browserLang) ? browserLang : "en";
};

// The landing site hands off the visitor's chosen language as a ?lang= query
// param (it lives on a different origin, so it can't share localStorage).
// Honour it over the stored/browser default so the app opens in the same
// language the visitor was reading the landing in.
const getUrlLanguage = () => {
  const urlLang = new URLSearchParams(window.location.search).get("lang");
  return urlLang && SUPPORTED_LANGUAGES.includes(urlLang) ? urlLang : null;
};

export function LanguageProvider({ children }) {
  const [language, setLanguage] = useState(() => {
    return (
      getUrlLanguage() ||
      safeStorage.get("preferredLanguage") ||
      getBrowserLanguage()
    );
  });
  const [translations, setTranslations] = useState({});

  useEffect(() => {
    safeStorage.set("preferredLanguage", language);
    loadTranslations(language);
  }, [language]);

  const loadTranslations = async (lang) => {
    try {
      const response = await import(`../locales/${lang}.json`);
      setTranslations(response.default);
    } catch (error) {
      console.error(`Failed to load translations for ${lang}:`, error);
      // Fallback to English if translation fails
      if (lang !== "en") {
        const enResponse = await import("../locales/en.json");
        setTranslations(enResponse.default);
      }
    }
  };

  const t = (key, variables) => {
    const keys = key.split(".");
    let value = translations;
    for (const k of keys) {
      value = value?.[k];
    }

    if (!value) return key;
    if (!variables) return value;

    return formatMessage(value, variables, language);
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export const useLanguage = () => useContext(LanguageContext);
